import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { UserPlus, Mail, MapPin, Clock, Car, CheckCircle2, AlertCircle, Navigation, Loader2, Key, Plane, Users, Search, Copy, Link, Phone, Plus, Trash2, Route } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Common US Airports with terminals/airlines
const AIRPORTS = [
  { code: 'JFK', name: 'John F. Kennedy International Airport', city: 'New York' },
  { code: 'LAX', name: 'Los Angeles International Airport', city: 'Los Angeles' },
  { code: 'ORD', name: "O'Hare International Airport", city: 'Chicago' },
  { code: 'DFW', name: 'Dallas/Fort Worth International Airport', city: 'Dallas' },
  { code: 'DEN', name: 'Denver International Airport', city: 'Denver' },
  { code: 'ATL', name: 'Hartsfield-Jackson Atlanta International Airport', city: 'Atlanta' },
  { code: 'SFO', name: 'San Francisco International Airport', city: 'San Francisco' },
  { code: 'SEA', name: 'Seattle-Tacoma International Airport', city: 'Seattle' },
  { code: 'MIA', name: 'Miami International Airport', city: 'Miami' },
  { code: 'BOS', name: 'Boston Logan International Airport', city: 'Boston' },
  { code: 'LAS', name: 'Harry Reid International Airport', city: 'Las Vegas' },
  { code: 'MCO', name: 'Orlando International Airport', city: 'Orlando' },
  { code: 'PHX', name: 'Phoenix Sky Harbor International Airport', city: 'Phoenix' },
  { code: 'IAH', name: 'George Bush Intercontinental Airport', city: 'Houston' },
  { code: 'EWR', name: 'Newark Liberty International Airport', city: 'Newark' },
];

// Common Airlines
const AIRLINES = [
  'American Airlines',
  'Delta Air Lines', 
  'United Airlines',
  'Southwest Airlines',
  'JetBlue Airways',
  'Alaska Airlines',
  'Spirit Airlines',
  'Frontier Airlines',
  'Hawaiian Airlines',
  'Allegiant Air',
  'Sun Country Airlines',
  'Other'
];

export default function AdminCreateRide() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [contacts, setContacts] = useState([]);
  const [showContactSearch, setShowContactSearch] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  
  // Multi-trip state - now includes wait_time for time spent at each stop
  const [trips, setTrips] = useState([
    { id: 1, pickup_location: '', dropoff_location: '', miles_driven: '', time_hours: '', wait_time: '0', calculating: false }
  ]);
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    date: '',
    time: '',
    notes: '',
    discount_percent: '',
    // Airport pickup fields (for first trip)
    is_airport_pickup: false,
    pickup_airport: '',
    pickup_airline: '',
    pickup_terminal: '',
    pickup_flight_number: '',
    // Airport dropoff fields (for last trip)
    is_airport_dropoff: false,
    dropoff_airport: '',
    dropoff_airline: '',
    dropoff_terminal: ''
  });
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [addressSuggestions, setAddressSuggestions] = useState({});
  const [showSuggestions, setShowSuggestions] = useState({});

  // Calculate distance and time for a specific trip leg using Google Maps
  const calculateTripDistance = async (tripId, pickup, dropoff) => {
    if (!pickup || !dropoff || !window.google) return;
    
    // Mark trip as calculating
    setTrips(prev => prev.map(t => 
      t.id === tripId ? { ...t, calculating: true } : t
    ));
    
    try {
      const service = new window.google.maps.DistanceMatrixService();
      service.getDistanceMatrix({
        origins: [pickup],
        destinations: [dropoff],
        travelMode: 'DRIVING',
        unitSystem: window.google.maps.UnitSystem.IMPERIAL
      }, (response, status) => {
        if (status === 'OK' && response.rows[0].elements[0].status === 'OK') {
          const element = response.rows[0].elements[0];
          const distanceText = element.distance.text;
          const miles = parseFloat(distanceText.replace(' mi', '').replace(',', ''));
          const durationInSeconds = element.duration.value;
          const hours = (durationInSeconds / 3600).toFixed(2);
          
          setTrips(prev => prev.map(t => 
            t.id === tripId ? { 
              ...t, 
              miles_driven: miles.toString(), 
              time_hours: hours,
              calculating: false 
            } : t
          ));
        } else {
          setTrips(prev => prev.map(t => 
            t.id === tripId ? { ...t, calculating: false } : t
          ));
        }
      });
    } catch (error) {
      console.error('Error calculating trip distance:', error);
      setTrips(prev => prev.map(t => 
        t.id === tripId ? { ...t, calculating: false } : t
      ));
    }
  };

  // Add a new trip leg
  const addTrip = () => {
    const lastTrip = trips[trips.length - 1];
    setTrips([
      ...trips,
      { 
        id: Date.now(), 
        pickup_location: lastTrip.dropoff_location, // Auto-fill with last dropoff
        dropoff_location: '', 
        miles_driven: '', 
        time_hours: '',
        wait_time: '0',
        calculating: false
      }
    ]);
  };

  const addReturnTrip = () => {
    const first = trips[0];
    setTrips([
      ...trips,
      {
        id: Date.now(),
        pickup_location: first.dropoff_location,
        dropoff_location: first.pickup_location,
        miles_driven: first.miles_driven,
        time_hours: first.time_hours,
        wait_time: '0',
        calculating: false
      }
    ]);
  };

  const getDiscount = () => {
    if (!calculatedPrice) return { percent: 0, amount: 0, total: 0 };
    const percent = Math.min(100, Math.max(0, parseFloat(formData.discount_percent) || 0));
    const amount = Math.round(calculatedPrice.final_price * percent) / 100;
    return { percent, amount, total: Math.round((calculatedPrice.final_price - amount) * 100) / 100 };
  };

  // Remove a trip leg
  const removeTrip = (id) => {
    if (trips.length > 1) {
      setTrips(trips.filter(t => t.id !== id));
    }
  };

  // Update a specific trip and auto-calculate if both locations are filled
  const updateTrip = (id, field, value) => {
    setTrips(prev => {
      const updated = prev.map(t => 
        t.id === id ? { ...t, [field]: value } : t
      );
      
      // If updating pickup or dropoff, trigger distance calculation
      if (field === 'pickup_location' || field === 'dropoff_location') {
        const trip = updated.find(t => t.id === id);
        if (trip) {
          const pickup = field === 'pickup_location' ? value : trip.pickup_location;
          const dropoff = field === 'dropoff_location' ? value : trip.dropoff_location;
          
          // Auto-calculate when both locations are filled (with some delay for typing)
          if (pickup && dropoff && pickup.length > 5 && dropoff.length > 5) {
            setTimeout(() => {
              calculateTripDistance(id, pickup, dropoff);
            }, 1000);
          }
        }
      }
      
      return updated;
    });
  };

  // Calculate total miles, drive time, and wait time
  const getTotalMilesAndTime = () => {
    const totalMiles = trips.reduce((sum, t) => sum + (parseFloat(t.miles_driven) || 0), 0);
    const totalDriveTime = trips.reduce((sum, t) => sum + (parseFloat(t.time_hours) || 0), 0);
    const totalWaitTime = trips.reduce((sum, t) => sum + (parseFloat(t.wait_time) || 0), 0);
    const totalTime = totalDriveTime + totalWaitTime;
    return { totalMiles, totalDriveTime, totalWaitTime, totalTime };
  };

  // Fetch existing contacts/riders on mount
  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/contacts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    }
  };

  const selectContact = (contact) => {
    setFormData(prev => ({
      ...prev,
      client_name: contact.name || '',
      client_email: contact.email || '',
      client_phone: contact.phone || ''
    }));
    setShowContactSearch(false);
    setContactSearch('');
    toast.success(`Selected: ${contact.name || contact.email}`);
  };

  const filteredContacts = contacts.filter(c => {
    const search = contactSearch.toLowerCase();
    return (
      (c.name && c.name.toLowerCase().includes(search)) ||
      (c.email && c.email.toLowerCase().includes(search)) ||
      (c.phone && c.phone.includes(search))
    );
  });

  // Debounced address search using Google Geocoding API
  const searchAddress = useCallback(async (query, type) => {
    if (!query || query.length < 3) {
      setAddressSuggestions(prev => ({ ...prev, [type]: [] }));
      return;
    }

    try {
      // Try Google Places Autocomplete first
      if (window.google && window.google.maps && window.google.maps.places) {
        const service = new window.google.maps.places.AutocompleteService();
        service.getPlacePredictions(
          { 
            input: query, 
            componentRestrictions: { country: 'us' },
            types: ['address']
          },
          (predictions, status) => {
            if (status === 'OK' && predictions) {
              setAddressSuggestions(prev => ({
                ...prev,
                [type]: predictions.map(p => ({
                  description: p.description,
                  placeId: p.place_id
                }))
              }));
              setShowSuggestions(prev => ({ ...prev, [type]: true }));
            }
          }
        );
      }
    } catch (err) {
      console.error('Address search error:', err);
    }
  }, []);

  // Calculate distance when addresses change
  useEffect(() => {
    const pickup = formData.is_airport_pickup 
      ? `${formData.pickup_airport} Airport ${formData.pickup_terminal ? `Terminal ${formData.pickup_terminal}` : ''}, ${formData.pickup_airline || ''}`
      : trips[0]?.pickup_location;
    
    const dropoff = formData.is_airport_dropoff
      ? `${formData.dropoff_airport} Airport ${formData.dropoff_terminal ? `Terminal ${formData.dropoff_terminal}` : ''}`
      : trips[trips.length - 1]?.dropoff_location;

    // Only auto-calculate for single trip
    if (trips.length === 1 && pickup && dropoff && window.google) {
      calculateDistanceAndTime(pickup, dropoff);
    }
  }, [
    trips,
    formData.pickup_airport,
    formData.dropoff_airport,
    formData.pickup_terminal,
    formData.dropoff_terminal,
    formData.is_airport_pickup,
    formData.is_airport_dropoff
  ]);

  // Recalculate total price when trips change
  useEffect(() => {
    const { totalMiles, totalTime } = getTotalMilesAndTime();
    if (totalMiles > 0 && totalTime > 0) {
      calculatePrice(totalMiles, totalTime);
    }
  }, [trips]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Auto-calculate price when distance/time changes manually
    if (name === 'miles_driven' || name === 'time_hours') {
      const miles = name === 'miles_driven' ? parseFloat(value) : parseFloat(formData.miles_driven);
      const hours = name === 'time_hours' ? parseFloat(value) : parseFloat(formData.time_hours);
      
      if (miles && hours) {
        calculatePrice(miles, hours);
      }
    }

    // Search for address suggestions
    if (name === 'pickup_location') {
      searchAddress(value, 'pickup');
    } else if (name === 'dropoff_location') {
      searchAddress(value, 'dropoff');
    }
  };

  const selectAddress = (address, type) => {
    const fieldName = type === 'pickup' ? 'pickup_location' : 'dropoff_location';
    setFormData(prev => ({ ...prev, [fieldName]: address }));
    setShowSuggestions(prev => ({ ...prev, [type]: false }));
  };

  const calculateDistanceAndTime = async (pickup, dropoff) => {
    if (!pickup || !dropoff || !window.google) return;
    
    try {
      const service = new window.google.maps.DistanceMatrixService();
      service.getDistanceMatrix({
        origins: [pickup],
        destinations: [dropoff],
        travelMode: 'DRIVING',
        unitSystem: window.google.maps.UnitSystem.IMPERIAL
      }, async (response, status) => {
        if (status === 'OK' && response.rows[0].elements[0].status === 'OK') {
          const element = response.rows[0].elements[0];
          const distanceText = element.distance.text;
          const miles = parseFloat(distanceText.replace(' mi', '').replace(',', ''));
          const durationInSeconds = element.duration.value;
          const hours = (durationInSeconds / 3600).toFixed(2);
          
          setFormData(prev => ({ 
            ...prev, 
            miles_driven: miles.toString(), 
            time_hours: hours.toString() 
          }));
          
          await calculatePrice(miles, parseFloat(hours));
        }
      });
    } catch (error) {
      console.error('Error calculating distance:', error);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setGettingLocation(true);
    setLocationError('');

    const timeoutId = setTimeout(() => {
      setGettingLocation(false);
      setLocationError('Location request timed out. Please enter address manually.');
    }, 15000);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        clearTimeout(timeoutId);
        const { latitude, longitude } = position.coords;
        
        // Try Google Geocoding first
        if (window.google && window.google.maps) {
          try {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode(
              { location: { lat: latitude, lng: longitude } },
              (results, status) => {
                setGettingLocation(false);
                if (status === 'OK' && results[0]) {
                  setFormData(prev => ({
                    ...prev,
                    pickup_location: results[0].formatted_address
                  }));
                } else {
                  // Fallback to coordinates
                  setFormData(prev => ({
                    ...prev,
                    pickup_location: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
                  }));
                }
              }
            );
          } catch (err) {
            setGettingLocation(false);
            setFormData(prev => ({
              ...prev,
              pickup_location: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
            }));
          }
        } else {
          // No Google Maps, use coordinates
          setGettingLocation(false);
          setFormData(prev => ({
            ...prev,
            pickup_location: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
          }));
        }
      },
      (error) => {
        clearTimeout(timeoutId);
        setGettingLocation(false);
        let errorMsg = 'Unable to get your location.';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = 'Location permission denied. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = 'Location information unavailable.';
            break;
          case error.TIMEOUT:
            errorMsg = 'Location request timed out.';
            break;
          default:
            errorMsg = 'An unknown error occurred.';
        }
        setLocationError(errorMsg);
      },
      { 
        enableHighAccuracy: true, 
        timeout: 10000,
        maximumAge: 60000 
      }
    );
  };

  const calculatePrice = async (miles, hours) => {
    try {
      const response = await fetch(`${API_URL}/api/calculate-price`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          distance_miles: miles,
          time_hours: hours
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCalculatedPrice(data);
      }
    } catch (err) {
      console.error('Price calculation failed:', err);
    }
  };

  const buildFullAddress = (type) => {
    if (type === 'pickup' && formData.is_airport_pickup) {
      const airport = AIRPORTS.find(a => a.code === formData.pickup_airport);
      let address = airport ? airport.name : formData.pickup_airport;
      if (formData.pickup_terminal) address += `, Terminal ${formData.pickup_terminal}`;
      if (formData.pickup_airline) address += ` (${formData.pickup_airline})`;
      if (formData.pickup_flight_number) address += ` - Flight ${formData.pickup_flight_number}`;
      return address;
    }
    if (type === 'dropoff' && formData.is_airport_dropoff) {
      const airport = AIRPORTS.find(a => a.code === formData.dropoff_airport);
      let address = airport ? airport.name : formData.dropoff_airport;
      if (formData.dropoff_terminal) address += `, Terminal ${formData.dropoff_terminal}`;
      if (formData.dropoff_airline) address += ` (${formData.dropoff_airline})`;
      return address;
    }
    return type === 'pickup' ? formData.pickup_location : formData.dropoff_location;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Combine date and time
      const dateTime = new Date(`${formData.date}T${formData.time}`);
      
      // Build trip details for notes and location
      const { totalMiles, totalTime } = getTotalMilesAndTime();
      
      // Build pickup (first trip) and dropoff (last trip) addresses
      let pickupAddress = trips[0].pickup_location;
      let dropoffAddress = trips[trips.length - 1].dropoff_location;
      
      // Apply airport formatting if needed
      if (formData.is_airport_pickup) {
        const airport = AIRPORTS.find(a => a.code === formData.pickup_airport);
        pickupAddress = airport ? airport.name : formData.pickup_airport;
        if (formData.pickup_terminal) pickupAddress += `, Terminal ${formData.pickup_terminal}`;
        if (formData.pickup_airline) pickupAddress += ` (${formData.pickup_airline})`;
        if (formData.pickup_flight_number) pickupAddress += ` - Flight ${formData.pickup_flight_number}`;
      }
      
      if (formData.is_airport_dropoff) {
        const airport = AIRPORTS.find(a => a.code === formData.dropoff_airport);
        dropoffAddress = airport ? airport.name : formData.dropoff_airport;
        if (formData.dropoff_terminal) dropoffAddress += `, Terminal ${formData.dropoff_terminal}`;
        if (formData.dropoff_airline) dropoffAddress += ` (${formData.dropoff_airline})`;
      }

      // Build notes with trip details
      let notes = formData.notes || '';
      
      // Add multi-trip details to notes
      if (trips.length > 1) {
        notes = `=== MULTI-TRIP ORDER (${trips.length} trips) ===\n`;
        trips.forEach((trip, index) => {
          notes += `Trip ${index + 1}: ${trip.pickup_location} → ${trip.dropoff_location} (${trip.miles_driven || '?'} mi)\n`;
        });
        notes += `Total: ${totalMiles.toFixed(1)} miles, ${totalTime.toFixed(2)} hours\n`;
        notes += `========================\n`;
        if (formData.notes) notes += formData.notes;
      }
      
      if (formData.is_airport_pickup && formData.pickup_flight_number) {
        notes = `Flight: ${formData.pickup_flight_number} | Airline: ${formData.pickup_airline || 'N/A'} | Terminal: ${formData.pickup_terminal || 'N/A'}\n${notes}`;
      }
      
      // Use the appropriate endpoint - driver/create-ride works for both drivers and admins
      const response = await fetch(`${API_URL}/api/driver/create-ride`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          client_name: formData.client_name,
          client_email: formData.client_email || null,
          client_phone: formData.client_phone,
          discount_percent: parseFloat(formData.discount_percent) || 0,
          pickup_location: pickupAddress,
          dropoff_location: dropoffAddress,
          date: dateTime.toISOString(),
          miles_driven: totalMiles,
          time_hours: totalTime,
          notes: notes.trim(),
          trip_legs: trips.map((t, i) => ({
            leg_number: i + 1,
            pickup: t.pickup_location,
            dropoff: t.dropoff_location,
            miles: parseFloat(t.miles_driven) || 0,
            hours: parseFloat(t.time_hours) || 0
          }))
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data);
      } else {
        setError(data.detail || 'Failed to create ride');
      }
    } catch (err) {
      setError('Failed to create ride. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyPaymentLink = () => {
    const link = `${window.location.origin}/pay/${success.short_code || success.payment_token}`;
    navigator.clipboard.writeText(link);
    toast.success('Payment link copied to clipboard!');
  };

  if (success) {
    const paymentLink = `${window.location.origin}/pay/${success.short_code || success.payment_token}`;
    
    return (
      <div className="p-4 sm:p-6">
        <Card className="max-w-2xl mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-700">Ride Created Successfully!</CardTitle>
            <CardDescription data-testid="ride-success-message">
              {success.email_sent
                ? 'A payment link has been sent to the client\'s email.'
                : formData.client_email
                  ? 'Ride created, but email could not be sent.'
                  : 'No email on file - text or share the payment link below with the client.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Ride ID:</span>
                <span className="font-mono text-sm">{success.ride_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Client:</span>
                <span className="font-medium">{formData.client_name}</span>
              </div>
              {success.discount_amount > 0 && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span>${success.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-green-600" data-testid="success-discount-row">
                    <span>Discount ({success.discount_percent}%):</span>
                    <span>-${success.discount_amount.toFixed(2)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Amount:</span>
                <span className="font-bold text-green-600 text-xl" data-testid="success-amount">${success.amount.toFixed(2)}</span>
              </div>
            </div>

            {/* Payment Link Section - Prominent */}
            <div className="bg-purple-50 border-2 border-purple-200 p-4 rounded-lg" data-testid="payment-link-section">
              <div className="flex items-center gap-2 mb-3">
                <Link className="h-5 w-5 text-purple-600" />
                <span className="font-semibold text-purple-800">Payment Link</span>
              </div>
              <div className="flex gap-2">
                <Input 
                  value={paymentLink} 
                  readOnly 
                  className="text-sm bg-white"
                  data-testid="payment-link-input"
                />
                <Button 
                  onClick={copyPaymentLink}
                  variant="outline"
                  className="shrink-0"
                  data-testid="copy-payment-link-btn"
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </Button>
              </div>
              <p className="text-xs text-purple-600 mt-2">
                Share this link with the client to complete payment
              </p>
            </div>

            {/* Trip Breakdown */}
            {trips.length > 1 && (
              <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Route className="h-5 w-5 text-purple-600" />
                  <span className="font-semibold text-gray-800">Trip Breakdown ({trips.length} trips)</span>
                </div>
                <div className="space-y-3">
                  {trips.map((trip, index) => (
                    <div key={trip.id} className="flex items-center gap-3 p-2 bg-white rounded border">
                      <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                        {index + 1}
                      </span>
                      <div className="flex-1 text-sm">
                        <span className="text-green-600">{trip.pickup_location || 'Pickup'}</span>
                        <span className="mx-2 text-gray-400">→</span>
                        <span className="text-red-600">{trip.dropoff_location || 'Dropoff'}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {trip.miles_driven || '?'} mi
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between text-sm">
                  <span className="text-gray-600">Total:</span>
                  <span className="font-medium">{getTotalMilesAndTime().totalMiles.toFixed(1)} mi, {(getTotalMilesAndTime().totalTime * 60).toFixed(0)} min</span>
                </div>
              </div>
            )}

            {success.new_account_created && success.temporary_password && (
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <Key className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800">New Account Created</p>
                    <p className="text-sm text-blue-700 mt-1">
                      A new rider account was created for this client. Their login credentials are:
                    </p>
                    <div className="mt-2 p-2 bg-white rounded text-sm">
                      <div><strong>Email:</strong> {formData.client_email}</div>
                      <div><strong>Temporary Password:</strong> <code className="bg-gray-100 px-1 rounded">{success.temporary_password}</code></div>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      These credentials have been included in the email sent to the client.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!success.email_sent && (
              <div className="bg-gold-50 border border-gold-200 p-4 rounded-lg flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-gold-600 mt-0.5" />
                <div>
                  <p className="font-medium text-gold-800">Email not sent</p>
                  <p className="text-sm text-gold-700">
                    You can manually share this payment link with the client:
                  </p>
                  <code className="block mt-2 p-2 bg-white rounded text-xs break-all">
                    {`${window.location.origin}/pay/${success.payment_token}`}
                  </code>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button 
                onClick={() => {
                  setSuccess(null);
                  setTrips([{ id: 1, pickup_location: '', dropoff_location: '', miles_driven: '', time_hours: '', wait_time: '0', calculating: false }]);
                  setFormData({
                    client_name: '',
                    client_email: '',
                    client_phone: '',
                    date: '',
                    time: '',
                    notes: '',
                    is_airport_pickup: false,
                    pickup_airport: '',
                    pickup_airline: '',
                    pickup_terminal: '',
                    pickup_flight_number: '',
                    is_airport_dropoff: false,
                    dropoff_airport: '',
                    dropoff_airline: '',
                    dropoff_terminal: ''
                  });
                  setCalculatedPrice(null);
                }}
                className="flex-1"
              >
                Create Another Ride
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/rides')}
                className="flex-1"
              >
                View All Rides
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <UserPlus className="h-6 w-6" />
            Create Ride for Client
          </h1>
          <p className="text-gray-600 mt-1">
            Create a ride and send a payment link to the client via email
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {error}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Ride Details</CardTitle>
            <CardDescription>
              Fill in the client and ride information below. If the client doesn&apos;t have an account, one will be created automatically.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Client Information */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-900 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Client Information
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowContactSearch(!showContactSearch)}
                    data-testid="select-contacts-btn"
                  >
                    <Users className="h-4 w-4 mr-1" />
                    {showContactSearch ? 'Hide Contacts' : 'Select from Contacts'}
                  </Button>
                </div>

                {/* Contact Search */}
                {showContactSearch && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Search className="h-4 w-4 text-blue-600" />
                      <Input
                        placeholder="Search contacts by name, email, or phone..."
                        value={contactSearch}
                        onChange={(e) => setContactSearch(e.target.value)}
                        className="bg-white"
                        data-testid="contact-search-input"
                      />
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {filteredContacts.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-2">
                          {contacts.length === 0 ? 'No contacts found' : 'No matching contacts'}
                        </p>
                      ) : (
                        filteredContacts.slice(0, 10).map((contact, idx) => (
                          <div
                            key={idx}
                            onClick={() => selectContact(contact)}
                            className="flex items-center justify-between p-3 bg-white rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
                            data-testid={`contact-item-${idx}`}
                          >
                            <div>
                              <div className="font-medium">{contact.name || 'No name'}</div>
                              <div className="text-sm text-gray-500">{contact.email}</div>
                            </div>
                            {contact.phone && (
                              <div className="text-sm text-gray-400 flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {contact.phone}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="client_name">Client Name *</Label>
                    <Input
                      id="client_name"
                      name="client_name"
                      value={formData.client_name}
                      onChange={handleInputChange}
                      placeholder="John Smith"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client_email">Client Email (Optional)</Label>
                    <Input
                      id="client_email"
                      name="client_email"
                      type="email"
                      value={formData.client_email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      data-testid="client-email-input"
                    />
                    <p className="text-xs text-gray-500">
                      With an email, the payment link is emailed automatically. Without one, you&apos;ll get a short link to text.
                    </p>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="client_phone">Client Phone (Optional)</Label>
                    <Input
                      id="client_phone"
                      name="client_phone"
                      type="tel"
                      value={formData.client_phone}
                      onChange={handleInputChange}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Pickup Location */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-900 flex items-center gap-2">
                    <Route className="h-4 w-4 text-purple-600" />
                    Trip Details
                  </h3>
                  <div className="flex gap-2">
                    {trips.length === 1 && trips[0].pickup_location && trips[0].dropoff_location && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addReturnTrip}
                        className="text-purple-600 border-purple-300 hover:bg-purple-50"
                        data-testid="add-return-trip-btn"
                      >
                        <Route className="h-4 w-4 mr-1" />
                        Round Trip
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addTrip}
                      className="text-purple-600 border-purple-300 hover:bg-purple-50"
                      data-testid="add-trip-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Another Trip
                    </Button>
                  </div>
                </div>

                {trips.length > 1 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">
                    <strong>Multi-Trip Order:</strong> {trips.length} trips will be combined into a single payment
                  </div>
                )}

                {/* Trip Legs */}
                {trips.map((trip, index) => (
                  <div key={trip.id} className="border rounded-lg p-4 space-y-4 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-700 flex items-center gap-2">
                        <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                          Trip {index + 1}
                        </span>
                        {index === 0 && formData.is_airport_pickup && (
                          <span className="text-xs text-blue-600 flex items-center gap-1">
                            <Plane className="h-3 w-3" /> Airport Pickup
                          </span>
                        )}
                      </h4>
                      {trips.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTrip(trip.id)}
                          className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    {/* First trip - Airport pickup option */}
                    {index === 0 && (
                      <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                        <input
                          type="checkbox"
                          id="is_airport_pickup"
                          checked={formData.is_airport_pickup}
                          onChange={(e) => {
                            setFormData(prev => ({ 
                              ...prev, 
                              is_airport_pickup: e.target.checked
                            }));
                            if (e.target.checked) {
                              updateTrip(trip.id, 'pickup_location', '');
                            }
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600"
                        />
                        <Label htmlFor="is_airport_pickup" className="flex items-center gap-2 cursor-pointer">
                          <Plane className="h-4 w-4 text-blue-600" />
                          Picking up from Airport
                        </Label>
                      </div>
                    )}

                    {/* Airport pickup fields for first trip */}
                    {index === 0 && formData.is_airport_pickup ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-blue-200 rounded-lg bg-blue-50/50">
                        <div className="space-y-2">
                          <Label>Airport *</Label>
                          <Select
                            value={formData.pickup_airport}
                            onValueChange={(value) => setFormData(prev => ({ ...prev, pickup_airport: value }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select Airport" />
                            </SelectTrigger>
                            <SelectContent>
                              {AIRPORTS.map(airport => (
                                <SelectItem key={airport.code} value={airport.code}>
                                  {airport.code} - {airport.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Airline *</Label>
                          <Select
                            value={formData.pickup_airline}
                            onValueChange={(value) => setFormData(prev => ({ ...prev, pickup_airline: value }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select Airline" />
                            </SelectTrigger>
                            <SelectContent>
                              {AIRLINES.map(airline => (
                                <SelectItem key={airline} value={airline}>
                                  {airline}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Terminal</Label>
                          <Input
                            value={formData.pickup_terminal}
                            onChange={(e) => setFormData(prev => ({ ...prev, pickup_terminal: e.target.value }))}
                            placeholder="e.g., 4, B, International"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Flight Number</Label>
                          <Input
                            value={formData.pickup_flight_number}
                            onChange={(e) => setFormData(prev => ({ ...prev, pickup_flight_number: e.target.value.toUpperCase() }))}
                            placeholder="e.g., AA1234"
                            className="uppercase"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Pickup */}
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <MapPin className="h-3 w-3 text-green-600" />
                            Pickup Location *
                          </Label>
                          <Input
                            value={trip.pickup_location}
                            onChange={(e) => updateTrip(trip.id, 'pickup_location', e.target.value)}
                            placeholder="Enter pickup address"
                            required
                          />
                        </div>
                        
                        {/* Dropoff */}
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <MapPin className="h-3 w-3 text-red-600" />
                            Dropoff Location *
                          </Label>
                          <Input
                            value={trip.dropoff_location}
                            onChange={(e) => updateTrip(trip.id, 'dropoff_location', e.target.value)}
                            placeholder="Enter dropoff address"
                            required
                          />
                        </div>
                      </div>
                    )}

                    {/* Calculate Button */}
                    {trip.pickup_location && trip.dropoff_location && (
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => calculateTripDistance(trip.id, trip.pickup_location, trip.dropoff_location)}
                          disabled={trip.calculating}
                          className="text-blue-600 border-blue-300"
                        >
                          {trip.calculating ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Calculating...
                            </>
                          ) : (
                            <>
                              <Navigation className="h-3 w-3 mr-1" />
                              Calculate Distance & Time
                            </>
                          )}
                        </Button>
                        {trip.miles_driven && trip.time_hours && (
                          <span className="text-xs text-green-600">✓ Auto-calculated</span>
                        )}
                      </div>
                    )}

                    {/* Distance, Time, and Wait Time for this leg */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label className="text-sm">Distance (miles)</Label>
                        <Input
                          type="number"
                          value={trip.miles_driven}
                          onChange={(e) => updateTrip(trip.id, 'miles_driven', e.target.value)}
                          placeholder="Auto or manual"
                          min="0"
                          step="0.1"
                          className={trip.calculating ? 'animate-pulse bg-blue-50' : ''}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Drive Time (hrs)</Label>
                        <Input
                          type="number"
                          value={trip.time_hours}
                          onChange={(e) => updateTrip(trip.id, 'time_hours', e.target.value)}
                          placeholder="Auto or manual"
                          min="0"
                          step="any"
                          className={trip.calculating ? 'animate-pulse bg-blue-50' : ''}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm flex items-center gap-1">
                          <Clock className="h-3 w-3 text-orange-500" />
                          Wait Time (hrs)
                        </Label>
                        <Select
                          value={trip.wait_time}
                          onValueChange={(value) => updateTrip(trip.id, 'wait_time', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Wait time" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">No wait</SelectItem>
                            <SelectItem value="0.25">15 min</SelectItem>
                            <SelectItem value="0.5">30 min</SelectItem>
                            <SelectItem value="1">1 hour</SelectItem>
                            <SelectItem value="1.5">1.5 hours</SelectItem>
                            <SelectItem value="2">2 hours</SelectItem>
                            <SelectItem value="3">3 hours</SelectItem>
                            <SelectItem value="4">4 hours</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">Time at this stop</p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Totals Display */}
                {trips.length > 0 && (
                  <div className="bg-gray-100 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Total Distance:</span>
                      <span className="font-medium">{getTotalMilesAndTime().totalMiles.toFixed(1)} miles</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Total Drive Time:</span>
                      <span className="font-medium">{(getTotalMilesAndTime().totalDriveTime * 60).toFixed(0)} min</span>
                    </div>
                    {getTotalMilesAndTime().totalWaitTime > 0 && (
                      <div className="flex justify-between text-sm text-orange-600">
                        <span>Total Wait Time:</span>
                        <span className="font-medium">{(getTotalMilesAndTime().totalWaitTime * 60).toFixed(0)} min</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-bold border-t pt-2">
                      <span>Total Billable Time:</span>
                      <span>{getTotalMilesAndTime().totalTime.toFixed(2)} hours</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Date/Time */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Date & Time
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      name="date"
                      type="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="time">Time *</Label>
                    <Input
                      id="time"
                      name="time"
                      type="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Price Breakdown */}
              {calculatedPrice && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Price Breakdown</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-blue-600">Base Fare:</span>
                      <span>${calculatedPrice.base_fare.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-600">Distance ({getTotalMilesAndTime().totalMiles.toFixed(1)} mi × $6.50):</span>
                      <span>${calculatedPrice.distance_cost.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-600">Drive Time ({(getTotalMilesAndTime().totalDriveTime * 60).toFixed(0)} min × $0.65):</span>
                      <span>${(getTotalMilesAndTime().totalDriveTime * 60 * 0.65).toFixed(2)}</span>
                    </div>
                    {getTotalMilesAndTime().totalWaitTime > 0 && (
                      <div className="flex justify-between text-orange-600">
                        <span>Wait Time ({(getTotalMilesAndTime().totalWaitTime * 60).toFixed(0)} min × $0.65):</span>
                        <span>${(getTotalMilesAndTime().totalWaitTime * 60 * 0.65).toFixed(2)}</span>
                      </div>
                    )}
                    {trips.length > 1 && (
                      <div className="flex justify-between text-purple-600">
                        <span>Multi-Trip ({trips.length} trips):</span>
                        <span>Included</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-blue-200 pt-2 mt-2 font-medium text-blue-800">
                      <span>Subtotal:</span>
                      <span>${calculatedPrice.final_price.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3 text-green-700">
                      <Label htmlFor="discount_percent" className="text-green-700">Discount %</Label>
                      <Input
                        id="discount_percent"
                        name="discount_percent"
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={formData.discount_percent}
                        onChange={handleInputChange}
                        placeholder="0"
                        className="w-24 h-8 text-right bg-white"
                        data-testid="discount-percent-input"
                      />
                    </div>
                    {getDiscount().amount > 0 && (
                      <div className="flex justify-between text-green-700" data-testid="discount-row">
                        <span>Discount ({getDiscount().percent}%):</span>
                        <span>-${getDiscount().amount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-blue-200 pt-2 mt-2 font-bold text-blue-800 text-lg">
                      <span>Total:</span>
                      <span data-testid="price-total">${getDiscount().total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Any special instructions or notes about the ride..."
                  rows={3}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                size="lg"
                disabled={loading}
              >
                {loading ? 'Creating Ride...' : 'Create Ride & Send Payment Link'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
